import React from 'react';
import { X, Award, Star, ExternalLink, ArrowUpRight, ArrowRight, ArrowDownRight } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { AppState, ModuleId } from '../../types';
import { GroupId } from '../../config/systemConfig';
import { MODULES } from '../../data/questionLoader';
import { getRecentSessionsForLearner, getAllModuleTrends } from '../../utils/activityHelpers';
import { ActivityTimeline } from '../shared/ActivityTimeline';
interface StudentData {
  id: string;
  name: string;
  data: AppState;
}
interface StudentDetailPanelProps {
  student: StudentData;
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: ModuleId) => number;
  onClose: () => void;
  onViewDashboard?: (learnerId: string) => void;
}
export function StudentDetailPanel({
  student,
  selectedGroup,
  getModuleMastery,
  onClose,
  onViewDashboard
}: StudentDetailPanelProps) {
  const moduleMastery = MODULES.map(module => {
    const mastery = getModuleMastery(student.id, module.id);
    return {
      module,
      mastery
    };
  }).sort((a, b) => b.mastery - a.mastery);
  const recentSessions = getRecentSessionsForLearner(student.data.questionStates || {}, 3);
  const trends = getAllModuleTrends(student.data.familyMastery || {}, selectedGroup);
  const strengths = moduleMastery.filter(m => m.mastery >= 60).slice(0, 2);
  const weaknesses = [...moduleMastery].reverse().filter(m => m.mastery < 40).slice(0, 2);
  return <Card className="overflow-hidden sticky top-24 shadow-xl border-indigo-100 max-h-[calc(100vh-8rem)] flex flex-col" data-id="element-5187">
      <div className="bg-gradient-to-br from-indigo-500 to-violet-600 p-6 text-white relative flex-shrink-0" data-id="element-5188">
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 hover:bg-white/20 rounded-full transition-colors" data-id="element-5189">
          <X className="w-5 h-5" data-id="element-5190" />
        </button>

        <div className="flex items-center gap-4 mb-4" data-id="element-5191">
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-2xl font-bold border-2 border-white/40 shadow-inner" data-id="element-5192">
            {student.name.charAt(0).toUpperCase()}
          </div>
          <div data-id="element-5193">
            <h3 className="text-xl font-black mb-1" data-id="element-5194">{student.name}</h3>
            <div className="flex items-center gap-3 text-indigo-100 text-sm font-medium" data-id="element-5195">
              <span className="flex items-center gap-1" data-id="element-5196">
                <Star className="w-4 h-4 fill-current" data-id="element-5197" /> Level{' '}
                {student.data.stats.level}
              </span>
              <span data-id="element-5198">•</span>
              <span data-id="element-5199">{student.data.stats.xp} XP</span>
            </div>
          </div>
        </div>

        {onViewDashboard && <Button variant="secondary" size="sm" className="w-full bg-white/10 hover:bg-white/20 text-white border-white/20" onClick={() => onViewDashboard(student.id)} data-id="element-5200">
            <ExternalLink className="w-4 h-4 mr-2" data-id="element-5201" />
            Bekijk als leerling
          </Button>}
      </div>

      <div className="p-6 bg-slate-50 overflow-y-auto flex-1 space-y-8" data-id="element-5202">
        {/* Strengths & Weaknesses */}
        {(strengths.length > 0 || weaknesses.length > 0) && <div data-id="element-5203">
            <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-5204">
              Analyse
            </h4>
            <div className="grid grid-cols-2 gap-3" data-id="element-5205">
              {strengths.length > 0 && <div className="bg-emerald-50 p-3 rounded-xl border border-emerald-100" data-id="element-5206">
                  <div className="text-xs font-bold text-emerald-700 mb-1" data-id="element-5207">
                    Sterk in
                  </div>
                  {strengths.map(s => <div key={s.module.id} className="text-sm text-emerald-900 flex items-center gap-1" data-id="element-5208">
                      <span data-id="element-5209">{s.module.icon}</span> {s.module.title}
                    </div>)}
                </div>}
              {weaknesses.length > 0 && <div className="bg-rose-50 p-3 rounded-xl border border-rose-100" data-id="element-5210">
                  <div className="text-xs font-bold text-rose-700 mb-1" data-id="element-5211">
                    Moeite met
                  </div>
                  {weaknesses.map(w => <div key={w.module.id} className="text-sm text-rose-900 flex items-center gap-1" data-id="element-5212">
                      <span data-id="element-5213">{w.module.icon}</span> {w.module.title}
                    </div>)}
                </div>}
            </div>
          </div>}

        {/* Mastery Grid */}
        <div data-id="element-5214">
          <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-5215">
            Vakbeheersing
          </h4>

          <div className="space-y-3" data-id="element-5216">
            {moduleMastery.map(({
            module,
            mastery
          }) => {
            const trend = trends[module.id];
            return <div key={module.id} className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm" data-id="element-5217">
                  <div className="flex items-center justify-between mb-2" data-id="element-5218">
                    <div className="flex items-center gap-2" data-id="element-5219">
                      <span className="text-lg" data-id="element-5220">{module.icon}</span>
                      <span className="font-bold text-slate-700 text-sm flex items-center gap-1" data-id="element-5221">
                        {module.title}
                        {trend === 'up' && <ArrowUpRight className="w-3.5 h-3.5 text-emerald-500" data-id="element-5222" />}
                        {trend === 'down' && <ArrowDownRight className="w-3.5 h-3.5 text-rose-500" data-id="element-5223" />}
                        {trend === 'stable' && mastery > 0 && <ArrowRight className="w-3.5 h-3.5 text-slate-300" data-id="element-5224" />}
                      </span>
                    </div>
                    <span className={`text-sm font-black ${mastery >= 80 ? 'text-emerald-600' : mastery >= 50 ? 'text-amber-600' : 'text-rose-600'}`} data-id="element-5225">
                      {mastery}%
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden" data-id="element-5226">
                    <div className={`h-full rounded-full ${mastery >= 80 ? 'bg-emerald-500' : mastery >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{
                  width: `${mastery}%`
                }} data-id="element-5227" />
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Recent Activity */}
        <div data-id="element-5228">
          <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-5229">
            Recente Activiteit
          </h4>
          <ActivityTimeline sessions={recentSessions} data-id="element-5230" />
        </div>
      </div>
    </Card>;
}